import React from 'react';
import {Object3D, Vector3} from "three";
import Vector3Property from './Vector3Property';
import DefaultSceneManager from '../../classes/DefaultSceneManager';
import Scenario from '../../classes/scenarioElements/Scenario';
import {SyncStatuses} from '../../globals';


interface SyncedPropertyProps {
	objects?: Object3D[];
	getterFunc?: (obj: Object3D) => any;
	comparerFunc?: (a: any, b: any) => boolean;
	resyncFunction?: (objects: Object3D[], defaultValues: any[], currentValues: any[]) => void;
	children?: React.ReactNode;
	'data-name'?: string;
}

interface SyncedChildProps {
	synced?: boolean;
	showSyncStatus?: boolean;
	syncStatus?: typeof SyncStatuses[keyof typeof SyncStatuses];
	showResetButton?: boolean;
	contextMenuAddons?: any; // Replace 'any' with the appropriate type
	resetFunc?: () => void;
}

/**
 * a wrapper around object properties that is intended to work with the Default State mechanism
 *
 * displays the current synchronization status of the property, and a button to re-sync if it's out of sync
 * @export
 * @param {{
 * objects: [THREE.Object3D],
 * getterFunc: (),
 * comparerFunc: (a: *, b: *) Boolean,
 * resyncFunction: ()
 * }} props these special props may be used:
 * - objects: an array of objects this property is shared between, for use when multiple objects are being inspected *optional*
 * - getterFunc: a function that when executed, returns the current value of the property from the given objects
 * - comparerFunc: a function that, when executed, returns whether or not the current value of the property matches with the value of the property in the Default State
 * - resyncFunction: a function that, when executed, sets the property value to be the same as the Default State value. *defaults to simple a === b comparison. optional*
 * @returns
 */
export default function SyncedProperty(props: SyncedPropertyProps)
{
	const objects = props.objects !== undefined ? props.objects : [];
	const defaultValues: any[] = [];
	const currentValues: any[] = [];

	// if we're in the default state, don't display anything
	const isInDefaultState = Scenario.currentStep()?.isDefault();

	// if we're not a synced object, don't display anything
	let status:  typeof SyncStatuses[keyof typeof SyncStatuses] = SyncStatuses.Null;
	if (props.getterFunc)
	{
		for (let i = 0; i < objects.length; i++)
		{
			const obj = objects[i];

			// determine if object is synced
			const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(obj.userData.guid);
			if (!defaultObject)
			{
				status = SyncStatuses.Null;
				break;
			}

			const defaultValue = props.getterFunc(defaultObject);
			if (defaultValue !== undefined)
			{
				const objValue = props.getterFunc(obj);
				const valuesEqual = compareValues(objValue, defaultValue);

				switch (status)
				{
					case SyncStatuses.InSync:
						if (!valuesEqual)
							status = SyncStatuses.Mixed;
						break;
					case SyncStatuses.OutOfSync:
						if (valuesEqual)
							status = SyncStatuses.Mixed;
						break;
					case SyncStatuses.Mixed:
						break;
					case SyncStatuses.Null:
						status = valuesEqual ? SyncStatuses.InSync : SyncStatuses.OutOfSync;
						break;
					default:
						console.log("unknown syn status: " + JSON.stringify(status));
						break;
				}

				defaultValues.push(defaultValue);
				currentValues.push(objValue);
			}
		}
	}

	if (isInDefaultState || status === SyncStatuses.Null)
	{
		return props.children;
	}

	function compareValues(a, b)
	{
		if (props.comparerFunc && a !== undefined && b !== undefined)
		{
			return props.comparerFunc(a, b);
		}
		else return a === b;
	}

	function resync()
	{
		if (props.resyncFunction && props.objects)
		{
			props.resyncFunction(
				props.objects,
				[...defaultValues],
				[...currentValues],
			);
		}
	}

	function resyncVector3Axis(axis: 'x' | 'y' | 'z') 
	{
		if (props.resyncFunction && props.objects) 
		{
			const values: Vector3[] = [];
			for (let i = 0; i < currentValues.length; i++) 
			{
				values[i] = currentValues[i].clone();
				values[i][axis] = defaultValues[i][axis];
			}

			props.resyncFunction(
				props.objects,
				[...values],
				[...currentValues],
			);
		}
	}

	function Vector3PropertyInSync(axis)
	{
		for(let i = 0; i < currentValues.length; i++)
		{
			if(defaultValues[i][axis] !== currentValues[i][axis]) return false;
		}

		return true;
	}

	return (
		<div className={"syncedPropertyRoot"}
			data-name={props["data-name"] ? "synced-property-" + props["data-name"] + "-root" : "synced-property-root"}
		>
			{
				React.Children.map(props.children, (child, idx) =>
				{
					if (!React.isValidElement<SyncedChildProps>(child))
					{
						return child;
					}

					return React.cloneElement<SyncedChildProps>(child, {
						key: idx,
						synced: true,
						showSyncStatus: true,
						syncStatus: status,
						showResetButton: status === SyncStatuses.OutOfSync || status === SyncStatuses.Mixed,
						contextMenuAddons: status !== SyncStatuses.InSync && child.type === Vector3Property? 
							[
								... Vector3PropertyInSync('x') ? [] :
									[{	
										renderObject: "Sync X with Default State",
										id: "resyncX",
										callback: resyncVector3Axis,
										callbackParams: "x",
									}],
								... Vector3PropertyInSync('y') ? [] :
									[{	
										renderObject: "Sync Y with Default State",
										id: "resyncY",
										callback: resyncVector3Axis,
										callbackParams: "y",
									}],
								... Vector3PropertyInSync('z') ? [] :
									[{	
										renderObject: "Sync Z with Default State",
										id: "resyncZ",
										callback: resyncVector3Axis,
										callbackParams: "z",
									}],
							] : undefined,
							
						resetFunc: resync,
					});
				})
			}
		</div>
	);
}